import api from './api';

// Helper function to get projects
export const getProjectsForWidget = async () => {
  const response = await api.get('/projects');
  return response.data;
};

export interface WidgetAppearance {
  primaryColor?: string;
  secondaryColor?: string;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  size?: 'small' | 'medium' | 'large';
  logoUrl?: string;
  welcomeMessage?: string;
  placeholder?: string;
  autoOpen?: boolean;
  showBranding?: boolean;
}

export interface WidgetSettings {
  maxMessagesHistory?: number;
  rateLimitPerMin?: number;
  collectUserInfo?: boolean;
  userInfoFields?: string[];
  enableFileUpload?: boolean;
  enableFeedback?: boolean;
  offlineMessage?: string;
}

export type SourceType = 'website' | 'teams' | 'maximo' | 'slack' | 'simple-auth' | 'custom-sso';

export interface AuthConfig {
  sourceType: SourceType;
  websiteSettings?: {
    allowAnonymous?: boolean;
    collectUserInfo?: boolean;
    userInfoFields?: string[];
  };
  teamsSettings?: {
    clientId?: string;
    tenantId?: string;
    redirectUri?: string;
    scopes?: string[];
    captureTeamContext?: boolean;
    captureChannelContext?: boolean;
    allowedTenants?: string[];
  };
  maximoSettings?: {
    authUrl?: string;
    apiUrl?: string;
    loginType?: 'oauth' | 'basic' | 'form';
  };
  slackSettings?: {
    clientId?: string;
    redirectUri?: string;
    scopes?: string[];
    captureWorkspaceContext?: boolean;
    captureChannelContext?: boolean;
    allowedWorkspaces?: string[];
  };
  simpleAuthSettings?: {
    providers?: string[];
    required?: boolean;
    googleClientId?: string;
    microsoftClientId?: string;
    collectFields?: string[];
    rememberUser?: boolean;
    allowAnonymous?: boolean;
  };
  customSsoSettings?: {
    provider?: 'oauth2' | 'saml';
    loginUrl?: string;
    clientId?: string;
    redirectUri?: string;
    scopes?: string[];
  };
}

export interface LoggingConfig {
  logToConsole?: boolean;
  logLevel?: 'detailed' | 'basic' | 'none';
  includeIPAddress?: boolean;
  includeUserAgent?: boolean;
  includeReferrer?: boolean;
}

export interface Widget {
  id: string;
  name: string;
  description?: string;
  widgetKey: string;
  appearance: WidgetAppearance;
  settings: WidgetSettings;
  authConfig?: AuthConfig;
  logging?: LoggingConfig;
  projectId: string;
  organizationId: string;
  isActive: boolean;
  allowedDomains: string[];
  totalConversations: number;
  totalMessages: number;
  lastUsedAt?: string;
  createdAt: string;
  updatedAt: string;
  project?: {
    id: string;
    name: string;
  };
  _count?: {
    conversations: number;
  };
}

export interface CreateWidgetRequest {
  name: string;
  description?: string;
  projectId: string;
  ssoIntegrationId?: string; // NEW: Reference to SSO integration
  appearance?: WidgetAppearance;
  settings?: WidgetSettings;
  authConfig?: AuthConfig; // DEPRECATED: Use ssoIntegrationId instead
  logging?: LoggingConfig;
  allowedDomains?: string[];
}

export interface UpdateWidgetRequest {
  name?: string;
  description?: string;
  projectId?: string;
  appearance?: WidgetAppearance;
  settings?: WidgetSettings;
  authConfig?: AuthConfig;
  logging?: LoggingConfig;
  allowedDomains?: string[];
  isActive?: boolean;
}

export interface WidgetEmbedCode {
  widgetKey: string;
  scriptCode: string;
  htmlCode: string;
  instructions: string;
}

export interface WidgetAnalytics {
  widgetId: string;
  widgetName: string;
  totalConversations: number;
  totalMessages: number;
  activeConversations: number;
  averageMessagesPerConversation: number;
  lastUsedAt?: string;
}

export interface WidgetConversation {
  id: string;
  sessionId: string;
  widgetId: string;
  messages: Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
  }>;
  userInfo?: {
    name?: string;
    email?: string;
    [key: string]: any;
  };
  userAgent?: string;
  ipAddress?: string;
  referrer?: string;
  status: string;
  isResolved: boolean;
  messageCount: number;
  firstMessageAt?: string;
  lastMessageAt?: string;
  duration?: number;
  createdAt: string;
  updatedAt: string;
  closedAt?: string;
}

export interface WidgetConversationsResponse {
  conversations: WidgetConversation[];
  total: number;
  skip: number;
  take: number;
}

// Create a new widget
export const createWidget = async (data: CreateWidgetRequest): Promise<Widget> => {
  const response = await api.post('/widgets', data);
  return response.data;
};

// Get all widgets
export const getWidgets = async (): Promise<Widget[]> => {
  const response = await api.get('/widgets');
  return response.data;
};

// Get widget by ID
export const getWidgetById = async (widgetId: string): Promise<Widget> => {
  const response = await api.get(`/widgets/${widgetId}`);
  return response.data;
};

// Update widget
export const updateWidget = async (
  widgetId: string,
  data: UpdateWidgetRequest,
): Promise<Widget> => {
  const response = await api.put(`/widgets/${widgetId}`, data);
  return response.data;
};

// Delete widget
export const deleteWidget = async (widgetId: string): Promise<{ message: string }> => {
  const response = await api.delete(`/widgets/${widgetId}`);
  return response.data;
};

// Get widget embed code
export const getWidgetEmbedCode = async (widgetId: string): Promise<WidgetEmbedCode> => {
  const response = await api.get(`/widgets/${widgetId}/embed-code`);
  return response.data;
};

// Get widget analytics
export const getWidgetAnalytics = async (widgetId: string): Promise<WidgetAnalytics> => {
  const response = await api.get(`/widgets/${widgetId}/analytics`);
  return response.data;
};

// Get widget conversations
export const getWidgetConversations = async (
  widgetId: string,
  skip = 0,
  take = 20,
): Promise<WidgetConversationsResponse> => {
  const response = await api.get(`/widgets/${widgetId}/conversations`, {
    params: { skip, take },
  });
  return response.data;
};

// Get conversation by session ID
export const getConversationBySession = async (
  widgetId: string,
  sessionId: string,
): Promise<WidgetConversation> => {
  const response = await api.get(`/widgets/${widgetId}/conversations/${sessionId}`);
  return response.data;
};
